import type {InfiniteData} from '@tanstack/react-query';
import {useMemo} from 'react';
import {
  type GetEntryManagementsReturnValueType,
  useGetEntryManagement,
} from '../../../core/repositories/mypageEntryManagementsRepository';
import type {FilterValues} from '../constants/filter';
import {
  SORT_KEY,
  type SortKeyType,
  type SortOrderType,
} from '../constants/sort';
import {usePaginatedEntries} from './usePaginatedEntries';

type UseEntryManagementDataArgs = {
  entryManagementId?: number;
  sortBy?: SortKeyType;
  sortOrder?: SortOrderType;
  filterValues?: FilterValues;
  /** 企業名での絞り込み */
  companyName?: string;
  /** SP版かどうか（SP: ページング有効、PC: 全件取得） */
  isSp?: boolean;
  /** 認証済みかどうか（未ログイン時はデータフェッチを無効化） */
  isAuthenticated: boolean;
};

/**
 * ページング結果を統合形式に変換するヘルパー関数
 */
const convertPaginatedData = (
  queryData: InfiniteData<GetEntryManagementsReturnValueType> | undefined,
) => {
  if (!queryData) {
    return undefined;
  }

  const allEntries = queryData.pages.flatMap((page) => {
    if (page.ok) {
      return page.value.entries;
    }
    return [];
  });

  const lastPage = queryData.pages[queryData.pages.length - 1];
  if (!lastPage?.ok) {
    return {
      ok: false as const,
      error: 'UnknownError' as const,
    };
  }

  return {
    ok: true as const,
    value: {
      entries: allEntries,
      totalCount: lastPage.value.totalCount,
      page: 1,
      limit: 20,
    },
  };
};

/**
 * エントリー管理のデータ取得と計算ロジックを共通化したカスタムフック
 *
 * @description
 * - 企業名検索時はAPI検索を使用（ハイブリッドアプローチ）
 * - 企業名リスト用に全データも取得（Select用）
 * - SP版: 20件ずつページング、PC版: 全件取得
 * - latestStepDeadline/latestStepStartDateソート時はSP版でも全件取得
 *   （バックエンドがアプリケーション層ソートのためpage/limit指定時はソートが無効化されるため）
 */
export const useProgressMgtData = ({
  entryManagementId,
  sortBy,
  sortOrder,
  filterValues,
  companyName,
  isSp = false,
  isAuthenticated,
}: UseEntryManagementDataArgs) => {
  // latestStepでのソート時はページングを無効化（バックエンドがアプリ層ソートのため全件取得が必要）
  const isLatestStepSort =
    sortBy === SORT_KEY.LATEST_STEP_DEADLINE ||
    sortBy === SORT_KEY.LATEST_STEP_START_DATE;
  const shouldEnablePaging = isSp && !isLatestStepSort;

  // 全データ取得（企業名リスト用、常に全件取得）
  // 企業名リストは頻繁に変わらないため、5分間キャッシュを保持
  const allDataQuery = usePaginatedEntries({
    sortBy,
    sortOrder,
    overallStatus: filterValues?.overallStatus,
    hasCompletedEntry: filterValues?.hasCompletedEntry,
    hasLatestStep: filterValues?.hasLatestStep,
    isPagingEnabled: false,
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  });

  // 企業名検索結果取得（companyNameがある場合のみ）
  const searchQuery = usePaginatedEntries({
    sortBy,
    sortOrder,
    overallStatus: filterValues?.overallStatus,
    hasCompletedEntry: filterValues?.hasCompletedEntry,
    hasLatestStep: filterValues?.hasLatestStep,
    companyName,
    isPagingEnabled: shouldEnablePaging,
    enabled: isAuthenticated && !!companyName,
  });

  // 表示用データ取得
  const displayQuery = usePaginatedEntries({
    sortBy,
    sortOrder,
    overallStatus: filterValues?.overallStatus,
    hasCompletedEntry: filterValues?.hasCompletedEntry,
    hasLatestStep: filterValues?.hasLatestStep,
    isPagingEnabled: shouldEnablePaging,
    enabled: isAuthenticated && !companyName,
  });

  // 表示用クエリ: companyNameがあれば検索結果、なければ表示用データ
  const paginatedQuery = companyName ? searchQuery : displayQuery;

  const {data: entryDetailData} = useGetEntryManagement(
    entryManagementId || 0,
    {
      config: {
        enabled: isAuthenticated && !!entryManagementId,
      },
    },
  );

  const data = useMemo(
    () => convertPaginatedData(paginatedQuery.data),
    [paginatedQuery.data],
  );

  const allData = useMemo(
    () => convertPaginatedData(allDataQuery.data),
    [allDataQuery.data],
  );

  // totalCountはAPIレスポンスから取得
  const totalCount = data?.ok ? data.value.totalCount : 0;

  const nextStepName = useMemo(() => {
    if (!entryManagementId || entryManagementId <= 0 || !data?.ok) {
      return '';
    }

    const targetEntry = data.value.entries.find(
      (entry) => entry.id === entryManagementId,
    );

    return targetEntry?.latestSteps?.[0]?.stepName ?? '';
  }, [entryManagementId, data]);

  const existingCompanyIds = useMemo(() => {
    if (!allData?.ok) return [];
    return allData.value.entries
      .map((entry: {companyID: number | null}) => entry.companyID)
      .filter((id): id is number => id !== null);
  }, [allData]);

  // 既存の選考管理の企業名リスト（重複チェック用、現在の選考管理を除外）
  const existingCompanyNames = useMemo(() => {
    if (!allData?.ok) return [];
    return allData.value.entries
      .filter((entry) => entry.id !== entryManagementId)
      .map((entry) => entry.companyName)
      .filter((name): name is string => name !== null && name !== undefined);
  }, [allData, entryManagementId]);

  const selectedCompanyId = useMemo(() => {
    if (!entryDetailData?.ok) return undefined;
    const companyID = entryDetailData.value.companyID;
    return companyID !== null && companyID !== 0 ? companyID : undefined;
  }, [entryDetailData]);

  return {
    data,
    allData,
    totalCount,
    entryDetailData,
    nextStepName,
    existingCompanyIds,
    existingCompanyNames,
    selectedCompanyId,
    hasNextPage: paginatedQuery.hasNextPage,
    fetchNextPage: paginatedQuery.fetchNextPage,
    isFetchingNextPage: paginatedQuery.isFetchingNextPage,
    isLoading: paginatedQuery.isLoading || allDataQuery.isLoading,
    isError: paginatedQuery.isError || allDataQuery.isError,
  };
};
